import { type FC, useState } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";

import { CourseSelectionList } from "./CourseSelectionList";
import { MoveCoursesConfirmationModal } from "./MoveCoursesConfirmationModal";

import { COURSE_GROUPING_ID_QUERY_PARAM, COURSE_ID_QUERY_PARAM } from "components/constants";
import { t } from "i18n/i18n";
import { FormField } from "mds/components/FormField";
import { TextField } from "mds/components/TextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { store, storeApi } from "store/index";
import { courseSelectors } from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { toastSuccessMessage } from "utils/alerts";
import { ADMIN_COURSE_EVENT_PREFIX } from "utils/amplitude";
import { useQueryParamsActions } from "utils/routing";

type AddCourseGroupingModalProps = {
  onClose: () => void;
};

type AddCourseGroupingFormValues = {
  name: string;
  selectedCourseIds: string[];
};

export const AddCourseGroupingModal: FC<AddCourseGroupingModalProps> = ({ onClose }) => {
  const form = useForm<AddCourseGroupingFormValues>({
    defaultValues: {
      name: "",
      selectedCourseIds: [],
    },
  });
  const [confirmationCourseIds, setConfirmationCourseIds] = useState<string[]>([]);
  const courseIds = useListSelector(courseSelectors.selectIds);
  const { setQueryParams } = useQueryParamsActions();

  const selectedCourseIds = form.watch("selectedCourseIds");

  const handleCreateGrouping = async () => {
    const { name, selectedCourseIds: selectedIds } = form.getValues();
    const newGrouping = await storeApi.course_groupings.create({
      course_ids: selectedIds,
      parent_id: null,
      title: name.trim(),
    });

    toastSuccessMessage(t("home.toasts.grouping_created"));
    setQueryParams({
      [COURSE_GROUPING_ID_QUERY_PARAM]: newGrouping.id,
      [COURSE_ID_QUERY_PARAM]: null,
    });
    onClose();
  };

  const onSubmit: SubmitHandler<AddCourseGroupingFormValues> = async ({
    selectedCourseIds: selectedIds,
  }) => {
    const selectedCourseIdSet = new Set(selectedIds);
    const coursesFromOtherGroupings = courseSelectors
      .selectAll(store.getState())
      .filter((course) => selectedCourseIdSet.has(course.id) && course.grouping_id !== null);

    if (coursesFromOtherGroupings.length > 0) {
      setConfirmationCourseIds(coursesFromOtherGroupings.map((course) => course.id));
      return;
    }

    await handleCreateGrouping();
  };

  const toggleCourseChecked = (courseId: string) => {
    const currentIds = form.getValues("selectedCourseIds");
    const codeIndex = currentIds.indexOf(courseId);
    const newSelectedIds =
      codeIndex > -1 ? currentIds.filter((id) => id !== courseId) : [...currentIds, courseId];

    form.setValue("selectedCourseIds", newSelectedIds, { shouldDirty: true });
  };

  if (confirmationCourseIds.length > 0) {
    return (
      <MoveCoursesConfirmationModal
        courseIds={confirmationCourseIds}
        onCancel={() => setConfirmationCourseIds([])}
        onConfirm={handleCreateGrouping}
      />
    );
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-3">
          <FormField label={t("home.courses.course_grouping_title_label")} name="name" required>
            {(controlProps) => (
              <TextField
                {...form.register("name", {
                  required: t("error.field_required"),
                  validate: (value) => value.trim().length > 0 || t("error.field_required"),
                })}
                {...controlProps}
                eventName={`${ADMIN_COURSE_EVENT_PREFIX}Add Course Grouping`}
                placeholder={t("home.placeholder.course_grouping_name")}
              />
            )}
          </FormField>

          {courseIds.length > 0 && (
            <FormField
              label={t("home.courses.add_courses_to_grouping_label")}
              name="selectedCourseIds"
            >
              {() => (
                <CourseSelectionList
                  courseIds={courseIds}
                  selectedCourseIds={selectedCourseIds}
                  onCourseToggle={toggleCourseChecked}
                />
              )}
            </FormField>
          )}
        </div>

        <div className="mt-8 flex w-full justify-end gap-2">
          <SquareButton kind="tertiary" size="m" title={t("common.cancel")} onClick={onClose}>
            {t("common.cancel")}
          </SquareButton>

          <SquareButton
            kind="accent"
            size="m"
            title={t("common.add.course_grouping")}
            type="submit"
          >
            {t("common.add.course_grouping")}
          </SquareButton>
        </div>
      </form>
    </FormProvider>
  );
};
